// THE BRUSH PICTURE (owner 2026-10-28: "we shouldnt need to put a limit on that"): every line the buyer brushes on the
// Living Cover's crop step is drawn into one picture of their photo, however many there are, and sent as a PNG with
// the photo (strokes.py layer_of reads it). Each pixel says which stroke last went over it and with which tool:
// red and green, the stroke's number (high, low byte); blue, 255 the brush, 1 the other tool ('-'), 0 untouched.
// A tap is still sent as a point with its number ('#n'): the server finds the whole person or thing under it, and
// a line made after a tap wins over it where they meet, one made before it loses.

/** The longest side the brush picture is kept at: fine enough to clean an edge zoomed in close. */
export const CUT_EDIT_SIDE = 2048;

type Saved = { x: number; y: number; data: ImageData };

export class CutEdit {
  readonly canvas: HTMLCanvasElement;
  readonly w: number;
  readonly h: number;
  private g: CanvasRenderingContext2D;
  private scratch: HTMLCanvasElement;
  private sg: CanvasRenderingContext2D;
  private undos: Saved[] = [];
  /** How many lines are on it now. */
  lines = 0;

  constructor(picW: number, picH: number) {
    const k = Math.min(1, CUT_EDIT_SIDE / Math.max(picW, picH));
    this.w = Math.max(1, Math.round(picW * k));
    this.h = Math.max(1, Math.round(picH * k));
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.w;
    this.canvas.height = this.h;
    this.g = this.canvas.getContext('2d', { willReadFrequently: true })!;
    this.scratch = document.createElement('canvas');
    this.scratch.width = this.w;
    this.scratch.height = this.h;
    this.sg = this.scratch.getContext('2d', { willReadFrequently: true })!;
  }

  /**
   * One line, `pts` in 0..1 of the photo, `r` its width each side in the photo's pixels at the photo's own size scaled
   * to this picture (the caller converts). Laid on exactly (no soft edge: a pixel holds one stroke's number).
   */
  line(pts: [number, number][], erase: boolean, r: number, seq: number): void {
    if (!pts.length) return;
    const xs = pts.map((p) => p[0] * this.w);
    const ys = pts.map((p) => p[1] * this.h);
    const pad = Math.ceil(r) + 2;
    const x0 = Math.max(0, Math.floor(Math.min(...xs) - pad));
    const y0 = Math.max(0, Math.floor(Math.min(...ys) - pad));
    const x1 = Math.min(this.w, Math.ceil(Math.max(...xs) + pad));
    const y1 = Math.min(this.h, Math.ceil(Math.max(...ys) + pad));
    if (x1 <= x0 || y1 <= y0) return;
    const bw = x1 - x0;
    const bh = y1 - y0;
    this.sg.clearRect(x0, y0, bw, bh);
    this.sg.fillStyle = '#fff';
    this.sg.strokeStyle = '#fff';
    this.sg.lineWidth = 2 * r;
    this.sg.lineCap = 'round';
    this.sg.lineJoin = 'round';
    this.sg.beginPath();
    if (pts.length === 1) {
      this.sg.arc(xs[0], ys[0], r, 0, Math.PI * 2);
      this.sg.fill();
    } else {
      xs.forEach((x, i) => (i ? this.sg.lineTo(x, ys[i]) : this.sg.moveTo(x, ys[i])));
      this.sg.stroke();
    }
    const shape = this.sg.getImageData(x0, y0, bw, bh).data;
    const before = this.g.getImageData(x0, y0, bw, bh);
    const out = new ImageData(new Uint8ClampedArray(before.data), bw, bh);
    const hi = (seq >> 8) & 255;
    const lo = seq & 255;
    const b = erase ? 1 : 255;
    for (let i = 0; i < shape.length; i += 4) {
      if (shape[i + 3] < 128) continue;
      out.data[i] = hi;
      out.data[i + 1] = lo;
      out.data[i + 2] = b;
      out.data[i + 3] = 255;
    }
    this.g.putImageData(out, x0, y0);
    this.undos.push({ x: x0, y: y0, data: before });
    this.lines += 1;
  }

  /** The last line taken back. */
  undo(): boolean {
    const u = this.undos.pop();
    if (!u) return false;
    this.g.putImageData(u.data, u.x, u.y);
    this.lines = Math.max(0, this.lines - 1);
    return true;
  }

  clear(): void {
    this.g.clearRect(0, 0, this.w, this.h);
    this.undos = [];
    this.lines = 0;
  }

  /** What it holds now, to put back later (Cancel). */
  snapshot(): { data: ImageData; undos: Saved[]; lines: number } {
    return { data: this.g.getImageData(0, 0, this.w, this.h), undos: this.undos.slice(), lines: this.lines };
  }

  restore(s: { data: ImageData; undos: Saved[]; lines: number }): void {
    this.g.putImageData(s.data, 0, 0);
    this.undos = s.undos.slice();
    this.lines = s.lines;
  }

  /** The PNG to send, or null when nothing is brushed. */
  async blob(): Promise<Blob | null> {
    if (!this.lines) return null;
    return new Promise((res) => this.canvas.toBlob((b) => res(b), 'image/png'));
  }
}
